import { useMemo } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { Button, ManagedDataTable } from '@/design-system';
import type { Affiliate, AffiliateWithStats } from '../api/affiliates.service';

export interface AffiliateTableHandlers {
  onEdit: (affiliate: Affiliate) => void;
  onCoupons: (affiliate: Affiliate) => void;
  onDelete: (affiliate: Affiliate) => void;
  onCreate: () => void;
}

interface AffiliateTableProps {
  affiliates: AffiliateWithStats[];
  loading: boolean;
  handlers: AffiliateTableHandlers;
}

export function AffiliateTable({ affiliates, loading, handlers }: AffiliateTableProps) {
  const columns = useMemo<ColumnDef<AffiliateWithStats>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nome',
        cell: ({ row }) => <span className="text-sm font-medium text-gray-900">{row.original.name}</span>,
      },
      {
        accessorKey: 'email',
        header: 'E-mail',
        cell: ({ row }) => <span className="text-sm text-gray-600">{row.original.email}</span>,
      },
      {
        accessorKey: 'couponCount',
        header: 'Cupons',
        cell: ({ row }) => (
          <span className="text-sm text-gray-600">{row.original.couponCount ?? 0}</span>
        ),
      },
      {
        accessorKey: 'companiesCount',
        header: 'Empresas',
        cell: ({ row }) => (
          <span className="text-sm text-gray-600">{row.original.companiesCount ?? 0}</span>
        ),
      },
      {
        accessorKey: 'commissionPercent',
        header: 'Comissão',
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.commissionPercent ? `${row.original.commissionPercent}%` : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${
              row.original.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {row.original.isActive ? 'Ativo' : 'Inativo'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Ações',
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm"
              type="button"
              onClick={() => handlers.onEdit(row.original)}
              className="text-[#0084FF] hover:underline"
              title="Editar"
            >
              <Pencil size={14} /> Editar
            </Button>
            <Button variant="ghost" size="sm"
              type="button"
              onClick={() => handlers.onCoupons(row.original)}
              className="text-gray-700 hover:underline"
            >
              Cupons
            </Button>
            <Button variant="ghost" size="sm"
              type="button"
              onClick={() => handlers.onDelete(row.original)}
              className="text-red-600 hover:text-red-800"
              title="Excluir"
            >
              <Trash2 size={14} /> Excluir
            </Button>
          </div>
        ),
      },
    ],
    [handlers]
  );

  return (
    <ManagedDataTable
      columns={columns}
      data={affiliates}
      loading={loading}
      searchPlaceholder="Buscar afiliado..."
      toolbarButtons={[
        {
          label: 'Novo afiliado',
          icon: <Plus size={16} />,
          onClick: handlers.onCreate,
        },
      ]}
      clientPagination
      emptyMessage="Nenhum afiliado cadastrado."
    />
  );
}
